<script lang="ts" setup>
  import { toRef, toRefs } from 'vue';
  import TextInput from './TextInput.vue';
  import { useField } from 'vee-validate';
  import { InputWidthVariant } from '../InputWrapper/';
  import {
    type InputTransformValue,
    resolveInputTransform,
  } from '../inputTransforms/';
  import type { TextInputProps } from './TextInput.types.ts';

  export type TextInputFieldProps = TextInputProps & {
    rules?: (value: string) => string | boolean;
    transform?: InputTransformValue;
  };

  const props = withDefaults(defineProps<TextInputFieldProps>(), {
    value: undefined,
    rules: undefined,
    transform: undefined,
    type: 'text',
    width: InputWidthVariant.FULL,
  });

  const {
    name: inputName,
    label,
    rules,
    transform,
    ...rest
  } = toRefs<TextInputFieldProps>(props);
  // workaround to keep reactivity on rest value from toRefs
  const inputProps = toRef(rest);

  const {
    value: inputValue,
    errorMessage,
    handleBlur,
    handleChange,
    meta,
  } = useField<string>(inputName.value, rules?.value, {
    label: label?.value,
  });

  const onChange = (value: string) => {
    const transformValue = transform?.value;
    handleChange(
      transformValue ? resolveInputTransform(transformValue)(value) : value,
    );
  };

  const onBlur = () => {
    handleBlur(undefined, true);
  };
</script>

<template>
  <TextInput
    v-bind="inputProps"
    :error="errorMessage as string"
    :label="label"
    :meta="meta"
    :loading="loading"
    :model-value="inputValue as string"
    :name="name"
    :placeholder="placeholder"
    @blur="onBlur"
    @update:modelValue="onChange"
  >
    <template #iconRight> <slot name="iconRight"></slot> </template>
    <template #inputLabel> <slot name="inputLabel"></slot> </template>
    <template #inputError> <slot name="inputError"></slot> </template>
    <template #iconLeft> <slot name="iconLeft"></slot> </template>
  </TextInput>
</template>
